/**
 * The Tasks workspace's sidebar, laid out like the Events one: the
 * workspace's pages (Tasks, Projects), the boards as a list with their tile
 * and open count, then the management actions that used to crowd the tasks
 * header (labels, sections, CSV import, notification settings).
 *
 * A board is the `?board=` param the tasks page has always read, so every old
 * link still lands on the same board. The dialogs stay on the tasks page and
 * open from a `?manage=` param (`@/lib/tasks-nav`); only the labels popover
 * lives here, anchored to its item.
 */

import type { ReactNode } from "react"
import { Link, useLocation } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { Bell, FolderKanban, ListChecks, Settings, Settings2, Tags, Upload } from "lucide-react"
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { SidebarNavBadge } from "@/components/sidebar-nav-badge"
import { ManageTags } from "@/components/tag-picker"
import { BoardTile } from "@/components/tasks/task-glyphs"
import { BoardPrivateMark } from "@/components/tasks/board-settings"
import { useAuth } from "@/hooks/useAuth"
import { api } from "@/lib/api"
import { GENERAL_BOARD_KEY, memberBoards, type Board } from "@/lib/task-boards"
import { boardHref, boardKeyFromSearch, manageHref, readManageAction, type TasksManageAction } from "@/lib/tasks-nav"
import { isAdmin } from "@/lib/roles"

export function TasksSidebar({ hasLicenses, inProgressProjectCount }: { hasLicenses: boolean; inProgressProjectCount: number }) {
  const { pathname, search } = useLocation()
  const { user } = useAuth()
  const { isMobile, setOpenMobile } = useSidebar()
  const onTasks = (pathname === "/tasks" || pathname.startsWith("/tasks/")) && pathname !== "/tasks/settings"
  const boardKey = boardKeyFromSearch(search)
  const openAction = onTasks ? readManageAction(new URLSearchParams(search)) : null

  // Same key and fetcher as the tasks page, so the two share one request.
  const { data: boardsData } = useQuery({
    queryKey: ["boards"],
    queryFn: () => api.boards.list(),
    staleTime: 30_000,
  })
  const boards = memberBoards(Array.isArray(boardsData) ? (boardsData as Board[]) : [])
  // Someone who can see General alone gets no board list, as before. An
  // external person always sees theirs: there is no General to fall back on.
  const showBoards = boards.length > 1 || (!!user?.is_external && boards.length > 0)

  // On a phone the sidebar is a sheet over the page: close it once a choice
  // is made, or the list it just changed stays hidden behind it.
  const done = () => {
    if (isMobile) setOpenMobile(false)
  }

  const manageItem = (action: TasksManageAction, label: string, icon: ReactNode) => (
    <NavItem to={manageHref(action, pathname, search)} label={label} icon={icon} active={openAction === action} onClick={done} />
  )

  return (
    <>
      <SidebarGroup className="py-1">
        <SidebarGroupContent>
          <SidebarMenu>
            <NavItem
              to="/tasks"
              label="Tasks"
              icon={<ListChecks data-anim="checklist" />}
              // With boards listed, the board row carries the highlight.
              active={onTasks && !showBoards}
              onClick={done}
            />
            {/* Projects listed under a license before workspaces; it still needs one. */}
            {hasLicenses && (
              <NavItem
                to="/projects"
                label="Projects"
                icon={<FolderKanban data-anim="bars" />}
                active={pathname === "/projects" || pathname.startsWith("/projects/")}
                badge={<SidebarNavBadge count={inProgressProjectCount} />}
                onClick={done}
              />
            )}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {showBoards && (
        <SidebarGroup className="py-1">
          <SidebarGroupLabel>Boards</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu aria-label="Boards">
              {boards.map((board) => (
                <SidebarMenuItem key={board.key}>
                  <SidebarMenuButton asChild isActive={onTasks && board.key === boardKey} tooltip={board.name}>
                    <Link to={boardHref(board.key, search)} onClick={done}>
                      <BoardTile name={board.name} color={board.color} size={16} />
                      <span className="min-w-0 flex-1 truncate">{board.name}</span>
                      <BoardPrivateMark board={board} className="group-data-[collapsible=icon]:hidden" />
                      {/* Key and open count sit in fixed right-hand columns so they line up and never overlap a long name. */}
                      <span className="w-8 shrink-0 text-right font-mono text-[10.5px] font-medium text-[#989898] group-data-[collapsible=icon]:hidden dark:text-[#6e7679]">
                        {board.key}
                      </span>
                      <span className="w-7 shrink-0 text-right text-xs tabular-nums text-muted-foreground group-data-[collapsible=icon]:hidden">
                        {typeof board.open_count === "number" && board.open_count > 0 ? board.open_count : ""}
                      </span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}

      <SidebarGroup className="mt-auto py-1">
        <SidebarGroupLabel>Manage</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              {/* Labels belong to a board: the one the list is on. */}
              <ManageTags
                board={boardKey}
                side={isMobile ? "bottom" : "right"}
                trigger={
                  <SidebarMenuButton>
                    <Tags />
                    <span>Labels</span>
                  </SidebarMenuButton>
                }
              />
            </SidebarMenuItem>
            {/* Sections are General's, as the header button was. */}
            {isAdmin(user) && boardKey === GENERAL_BOARD_KEY && manageItem("sections", "Sections", <Settings2 />)}
            {/* CSV import files onto General, which an external person does not have. */}
            {!user?.is_external && manageItem("import", "Import CSV", <Upload />)}
            {manageItem("notifications", "Notifications", <Bell />)}
            <NavItem to="/tasks/settings" label="Settings" icon={<Settings />} active={pathname === "/tasks/settings"} onClick={done} />
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </>
  )
}

function NavItem({
  to,
  label,
  icon,
  active,
  badge,
  onClick,
}: {
  to: string
  label: string
  icon: ReactNode
  active: boolean
  badge?: ReactNode
  onClick?: () => void
}) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={active} tooltip={label}>
        <Link to={to} onClick={onClick}>
          {icon}
          <span>{label}</span>
          {badge}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}
